import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Info, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { fetchMaterialidadRiskCriteria } from "@/modules/tramites/materialidad/materialidadApi";
import {
  CRITICAL_BADGE_CLASS,
  RISK_BADGE_CLASS,
  RISK_LABELS,
} from "@/modules/tramites/materialidad/materialidadConstants";

function RiskLevelBadge({ level, critical }) {
  if (critical) {
    return (
      <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[10px] font-semibold ${CRITICAL_BADGE_CLASS}`}>
        Crítico
      </span>
    );
  }
  const label = RISK_LABELS[level] || level || "—";
  const cls = RISK_BADGE_CLASS[level] || RISK_BADGE_CLASS.bajo;
  return (
    <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

/**
 * Pestaña de criterios: por qué el despacho marca operaciones / faltantes.
 * Solo lectura; no modifica Visor SAT.
 */
export default function MaterialidadCriteriosRiesgoPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await fetchMaterialidadRiskCriteria();
      setData(payload);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron cargar los criterios de riesgo.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 p-4" data-testid="materialidad-criterios-panel">
        <Loader2 className="h-4 w-4 animate-spin" />
        Cargando criterios…
      </div>
    );
  }

  if (!data) {
    return (
      <p className="text-sm text-slate-600 m-0" data-testid="materialidad-criterios-panel">
        Sin criterios disponibles.
      </p>
    );
  }

  const criteria = data.criteria || [];
  const levels = data.risk_levels || [];
  const params = data.parameters || {};
  const notes = data.scope_notes || [];

  return (
    <div className="space-y-4" data-testid="materialidad-criterios-panel">
      <div className="rounded-md border border-slate-200 bg-white px-4 py-3 shadow-sm space-y-2">
        <div className="flex items-start gap-2">
          <ShieldAlert className="h-5 w-5 text-[#0F2D5C] shrink-0 mt-0.5" strokeWidth={1.8} />
          <div>
            <h2 className="text-sm font-bold text-[#0F2D5C] m-0">Criterios de riesgo del despacho</h2>
            <p className="text-xs text-slate-600 mt-1 m-0">{data.disclaimer}</p>
          </div>
        </div>
        {params.importe_alto_umbral_mxn != null ? (
          <p className="text-[11px] text-slate-500 m-0 pl-7">
            Piso / fallback de operación relevante:{" "}
            <span className="font-semibold text-slate-700">
              ${Number(params.importe_alto_umbral_mxn).toLocaleString("es-MX")} MXN
            </span>
            {params.importe_alto_mode_default ? (
              <>
                {" · "}Modo por defecto:{" "}
                <span className="font-semibold text-slate-700">
                  {params.importe_alto_mode_default === "relative"
                    ? `relativo (${params.importe_alto_pct_ingresos_mes_default ?? 2}% ingresos del mes)`
                    : "fijo"}
                </span>
              </>
            ) : null}
            {" · "}
            Hallazgos críticos:{" "}
            <span className="font-semibold text-slate-700">
              {(params.critical_flags || []).join(", ") || "—"}
            </span>
          </p>
        ) : null}
        {params.note ? (
          <p className="text-[11px] text-slate-500 m-0 pl-7">{params.note}</p>
        ) : null}
      </div>

      {notes.length > 0 ? (
        <div className="rounded-md border border-sky-200 bg-sky-50/70 px-4 py-3 text-xs text-sky-950 space-y-1.5">
          <p className="font-semibold m-0 flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5" />
            Cómo se usa la detección
          </p>
          <ul className="m-0 pl-4 list-disc space-y-1">
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {levels.length > 0 ? (
        <div className="grid gap-2 sm:grid-cols-3">
          {levels.map((row) => (
            <div key={row.id} className="rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <RiskLevelBadge level={row.id} />
              </div>
              <p className="text-[11px] text-slate-600 m-0">{row.meaning}</p>
            </div>
          ))}
        </div>
      ) : null}

      <section className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden" aria-label="Lista de criterios">
        <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/80 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <h3 className="text-sm font-semibold text-slate-800 m-0">Lista y consideración</h3>
          <span className="ml-auto text-[11px] text-slate-500">{criteria.length} criterio(s)</span>
        </div>
        <div className="divide-y divide-slate-100">
          {criteria.map((row) => (
            <article
              key={row.id}
              className="px-4 py-3 space-y-2"
              data-testid={`materialidad-criterio-${row.id}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-sm font-semibold text-slate-900 m-0">{row.label}</h4>
                <RiskLevelBadge level={row.risk_level} critical={Boolean(row.critical)} />
                <span className="text-[10px] font-mono text-slate-400">{row.id}</span>
              </div>
              <p className="text-[11px] text-slate-500 m-0">
                Aplica a: <span className="text-slate-700">{row.applies_to || "—"}</span>
              </p>
              <div className="grid gap-2 sm:grid-cols-2 text-xs">
                <div className="rounded-sm border border-slate-100 bg-slate-50/60 px-2.5 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0 mb-1">
                    Cuándo se considera
                  </p>
                  <p className="m-0 text-slate-800">{row.when}</p>
                </div>
                <div className="rounded-sm border border-slate-100 bg-slate-50/60 px-2.5 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0 mb-1">
                    Por qué importa
                  </p>
                  <p className="m-0 text-slate-800">{row.why}</p>
                </div>
              </div>
              {row.evidence_hint ? (
                <p className="text-[11px] text-slate-600 m-0">
                  <span className="font-semibold text-slate-700">Evidencia sugerida: </span>
                  {row.evidence_hint}
                </p>
              ) : null}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
